import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, CheckCircle, AlertTriangle, BarChart3, Users, Clock, Settings, ClipboardList, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { API } from '../../config/apiBase';


export default function EmpresaGestãoPage() {
    const [stats, setStats] = useState(null);
    const [empresa, setEmpresa] = useState(null);
    const [ajustes, setAjustes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        const h = { 'Authorization': `Bearer ${token}` };
        Promise.all([
            fetch(`${API}/ponto/dashboard/extended`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/empresa`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/ajustes`, { headers: h }).then(r => r.json()),
        ]).then(([s, e, aj]) => {
            setStats(s); setEmpresa(e); setAjustes(Array.isArray(aj) ? aj : []); setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const pendingAj = ajustes.filter(a => a.status === 'pendente');
    const usagePercent = stats?.max_employees > 0 ? Math.round((stats.total_employees / stats.max_employees) * 100) : 0;

    const quickActions = [
        { label: 'Cadastrar Colaborador', href: '/sistema/empresa/colaboradores', icon: Users, desc: 'Adicionar novo membro' },
        { label: 'Revisar Ponto', href: '/sistema/ponto', icon: Clock, desc: 'Verificar marcações do dia' },
        { label: 'Solicitações', href: '/sistema/empresa/solicitações', icon: ClipboardList, desc: `${pendingAj.length} pendente(s)` },
        { label: 'Permissões', href: '/sistema/empresa/permissões', icon: Shield, desc: 'Gerenciar acessos' },
        { label: 'Configurações', href: '/sistema/empresa/configurações', icon: Settings, desc: 'Dados da empresa' },
        { label: 'Performance', href: '/sistema/empresa/performance', icon: BarChart3, desc: 'Ver métricas' },
    ];

    return (
        <div className="space-y-5" data-testid="empresa-gestão">
            {/* Situação Atual */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Situação Atual da Operação</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                        { label: 'Presentes', value: stats?.present_today || 0, total: stats?.total_employees || 0, color: '#10B981' },
                        { label: 'Ausentes', value: stats?.absent_today || 0, total: stats?.total_employees || 0, color: '#EF4444' },
                        { label: 'Ajustes pendentes', value: pendingAj.length, total: null, color: '#F59E0B' },
                    ].map((item) => (
                        <div key={item.label} className="p-3 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <p className="text-xl font-bold" style={{ color: item.color }}>{item.value}</p>
                            <p className="text-[10px] sm:text-xs" style={{ color: 'var(--muted)' }}>
                                {item.label}{item.total !== null ? ` / ${item.total}` : ''}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Integridade Cadastro */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Integridade do Cadastro</h3>
                <div className="space-y-3">
                    <div>
                        <div className="flex justify-between mb-1">
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>Uso de Colaboradores</span>
                            <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{stats?.total_employees || 0} / {stats?.max_employees || 10}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(usagePercent, 100)}%`, background: usagePercent > 80 ? '#F59E0B' : '#10B981' }} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { label: 'Gestores', value: stats?.gestores || 0, ok: true },
                            { label: 'Geofence', value: stats?.geofence_enabled ? 'Ativo' : 'Inativo', ok: stats?.geofence_enabled },
                            { label: 'Jornadas', value: stats?.total_jornadas || 0, ok: (stats?.total_jornadas || 0) > 0 },
                            { label: 'Plano', value: empresa?.plan_status === 'active' ? 'Ativo' : 'Inativo', ok: empresa?.plan_status === 'active' },
                        ].map((item) => (
                            <div key={item.label} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                {item.ok ? <CheckCircle size={12} style={{ color: '#10B981' }} /> : <AlertTriangle size={12} style={{ color: '#F59E0B' }} />}
                                <div>
                                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{item.label}</p>
                                    <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{item.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Central de Ações Rapidas */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Central de Ações</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {quickActions.map((a) => (
                        <a key={a.label} href={a.href} className="flex items-center gap-3 p-3 rounded-lg transition-all hover:scale-[1.01]"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                                <a.icon size={15} style={{ color: 'var(--sv-gold-primary)' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{a.label}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{a.desc}</p>
                            </div>
                            <ArrowRight size={12} style={{ color: 'var(--muted)' }} className="shrink-0" />
                        </a>
                    ))}
                </div>
            </div>

            {/* Módulos Ativos */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Módulos Ativos</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                        { name: 'Ponto Digital', active: true },
                        { name: 'Geofence', active: stats?.geofence_enabled },
                        { name: 'Colaboradores', active: true },
                        { name: 'Relatórios', active: true },
                        { name: 'Escalas', active: (stats?.total_jornadas || 0) > 0 },
                        { name: 'Copilot IA', active: true },
                    ].map((m) => (
                        <div key={m.name} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: m.active ? '#10B981' : '#6B7280' }} />
                            <span className="text-xs" style={{ color: m.active ? 'var(--text)' : 'var(--muted)' }}>{m.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
